import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import type { ResponseData, PagedData, ListQueryRequest } from '../types';
import type {
  LoginRequest,
  LoginResponse,
  UserInfo,
  UserSummary,
  RoleSummary,
  Permission,
  CreateUserRequest,
  UpdateUserRequest,
  UpdatePasswordRequest,
  AssignRolesRequest,
  CreateRoleRequest,
  UpdateRoleRequest,
  UpdateRolePermissionsRequest,
} from '../types/auth';
import type { MenuItem } from '../types/common';

// ============ 基础配置 ============
const apiClient = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 统一响应提取器（兼容后端 PagedResult 的 totalCount -> frontend PagedData 的 total）
function extractData<T>(response: AxiosResponse<ResponseData<T>>): T {
  const data = response.data.data;
  if (data && typeof data === 'object' && 'totalCount' in (data as any) && !('total' in (data as any))) {
    (data as any).total = (data as any).totalCount;
  }
  return data;
}

// ============ 拦截器 ============

// 请求拦截器 - 添加 token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器 - 统一错误处理 + 分页字段兼容（backend 返回 totalCount，frontend 期望 total）
apiClient.interceptors.response.use(
  (response) => {
    const data = response.data;
    if (data && typeof data === 'object' && 'data' in data) {
      const payload = (data as any).data;
      if (payload && typeof payload === 'object' && 'totalCount' in payload && !('total' in payload)) {
        payload.total = payload.totalCount;
      }
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data;
    
    switch (status) {
      case 401:
        localStorage.removeItem('accessToken');
        localStorage.removeItem('tokenExpiry');
        window.location.href = '/login';
        break;
      case 403:
        console.error('没有权限访问该资源');
        break;
      case 404:
        console.error('请求的资源不存在');
        break;
      case 500:
        console.error('服务器内部错误');
        break;
    }
    
    return Promise.reject(data?.message || data || error.message || '请求失败');
  }
);

// ============ 认证 API ============
export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<ResponseData<LoginResponse>>('/auth/login', data);
    return extractData(response);
  },
  
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },
  
  getCurrentUser: async (): Promise<UserInfo> => {
    const response = await apiClient.get<ResponseData<UserInfo>>('/auth/me');
    return extractData(response);
  },
  
  getCaptcha: async (): Promise<{ captchaId: string; imageBase64: string }> => {
    const response = await apiClient.get<ResponseData<{ captchaId: string; imageBase64: string }>>('/auth/captcha');
    return extractData(response);
  },
  
  // 刷新Token
  refreshToken: async (): Promise<LoginResponse> => {
    const response = await apiClient.post<ResponseData<LoginResponse>>('/auth/refresh');
    return extractData(response);
  },
  
  // 获取当前用户菜单
  getMenu: async (): Promise<MenuItem[]> => {
    const response = await apiClient.get<ResponseData<MenuItem[]>>('/auth/menu');
    return extractData(response);
  },
};

// ============ 任务调度管理 API ============
export interface ScrapingJobStatus {
  enabled: boolean;
  jobId: string;
  cronExpression?: string;
  nextExecution?: string;
  lastExecution?: string;
  lastJobState?: string;
}

export const adminApi = {
  getScrapingJobStatus: async (): Promise<ScrapingJobStatus> => {
    const response = await apiClient.get<ResponseData<ScrapingJobStatus>>('/admin/jobs/scraping/status');
    return extractData(response);
  },
  enableScrapingJob: async (): Promise<string> => {
    const response = await apiClient.post<ResponseData<string>>('/admin/jobs/scraping/enable');
    return extractData(response);
  },
  disableScrapingJob: async (): Promise<string> => {
    const response = await apiClient.post<ResponseData<string>>('/admin/jobs/scraping/disable');
    return extractData(response);
  },
  triggerScrapingJob: async (): Promise<string> => {
    const response = await apiClient.post<ResponseData<string>>('/admin/jobs/scraping/trigger');
    return extractData(response);
  },
};

// ============ 内容分析 API ============
export const contentApi = {
  // 文章统计
  getArticles: async (params: ListQueryRequest & { 
    startDate?: string; 
    endDate?: string;
    category?: string;
    wechatAccountId?: string;
  }): Promise<PagedData<any>> => {
    const response = await apiClient.post<ResponseData<PagedData<any>>>('/content/articles/list', params);
    return extractData(response);
  },
  
  getArticleById: async (id: string): Promise<any> => {
    const response = await apiClient.get<ResponseData<any>>(`/content/articles/${id}`);
    return extractData(response);
  },
  
  getArticleStats: async (params: { startDate?: string; endDate?: string; wechatAccountId?: string }): Promise<any> => {
    const response = await apiClient.get<ResponseData<any>>('/content/articles/stats', { params });
    return extractData(response);
  },
  
  // 菜单分析
  getMenuStats: async (params: ListQueryRequest & { startDate?: string; endDate?: string }): Promise<PagedData<any>> => {
    const response = await apiClient.post<ResponseData<PagedData<any>>>('/content/menu/list', params);
    return extractData(response);
  },
  
  getMenuTrend: async (params: { days?: number }): Promise<any[]> => {
    const response = await apiClient.get<ResponseData<any[]>>('/content/menu/trend', { params });
    return extractData(response);
  },
  
  // 消息统计
  getMessageStats: async (params: ListQueryRequest & { startDate?: string; endDate?: string }): Promise<PagedData<any>> => {
    const response = await apiClient.post<ResponseData<PagedData<any>>>('/content/message/list', params);
    return extractData(response);
  },
  
  getMessageTypeDistribution: async (): Promise<any[]> => {
    const response = await apiClient.get<ResponseData<any[]>>('/content/message/distribution');
    return extractData(response);
  },
  
  // 央媒转发
  getCentralMediaReposts: async (params: ListQueryRequest): Promise<PagedData<any>> => {
    const response = await apiClient.post<ResponseData<PagedData<any>>>('/content/central-media/list', params);
    return extractData(response);
  },

  // 文章每日渠道统计
  getArticleDailyStats: async (params: ListQueryRequest & { articleId?: string; startDate?: string; endDate?: string; channel?: string }): Promise<PagedData<any>> => {
    const response = await apiClient.post<ResponseData<PagedData<any>>>('/content/articles/daily-stats', params);
    return extractData(response);
  },

  // 文章扩展属性
  getArticleExtensions: async (params: ListQueryRequest & { isOriginal?: boolean }): Promise<PagedData<any>> => {
    const response = await apiClient.get<ResponseData<PagedData<any>>>('/content/article-extensions', { params });
    return extractData(response);
  },

  // 消息关键词
  getMessageKeywords: async (params: ListQueryRequest & { keyword?: string; startDate?: string; endDate?: string }): Promise<PagedData<any>> => {
    const response = await apiClient.get<ResponseData<PagedData<any>>>('/content/message-keywords', { params });
    return extractData(response);
  },

  // 账号每日阅读来源
  getAccountReadSources: async (params: ListQueryRequest & { startDate?: string; endDate?: string; channel?: string }): Promise<PagedData<any>> => {
    const response = await apiClient.get<ResponseData<PagedData<any>>>('/channel/read-sources', { params });
    return extractData(response);
  },

  // 每日分享统计
  getShareStats: async (params: ListQueryRequest & { startDate?: string; endDate?: string }): Promise<PagedData<any>> => {
    const response = await apiClient.get<ResponseData<PagedData<any>>>('/content/share-stats', { params });
    return extractData(response);
  },

  // 文章评论
  getArticleComments: async (params: ListQueryRequest & { articleId?: string; sentiment?: string; startDate?: string; endDate?: string }): Promise<PagedData<any>> => {
    const response = await apiClient.post<ResponseData<PagedData<any>>>('/content/article-comments/list', params);
    return extractData(response);
  },

  // 内容主题统计
  getContentThemeStats: async (params: ListQueryRequest & { themeName?: string; dataYear?: number; dataMonth?: number }): Promise<PagedData<any>> => {
    const response = await apiClient.post<ResponseData<PagedData<any>>>('/content/theme-stats/list', params);
    return extractData(response);
  },

  // 文章用户画像
  getArticleUserDemographics: async (params: ListQueryRequest & { articleId?: string; demographicType?: string }): Promise<PagedData<any>> => {
    const response = await apiClient.post<ResponseData<PagedData<any>>>('/content/article-demographics/list', params);
    return extractData(response);
  },

  // 文章内容
  getArticleContent: async (articleId: string): Promise<any> => {
    const response = await apiClient.get<ResponseData<any>>(`/content/articles/${articleId}/content`);
    return extractData(response);
  },

  // 更新文章分类
  updateArticleCategory: async (articleId: string, category: string): Promise<any> => {
    const response = await apiClient.put<ResponseData<any>>(`/content/articles/${articleId}/category`, { category });
    return extractData(response);
  },

  // 批量重新分类文章（将不在白名单中的已有分类文章基于LLM重新分类）
  recategorizeArticles: async (limit: number = 50): Promise<any> => {
    const response = await apiClient.post<ResponseData<any>>('/content/articles/recategorize', { limit });
    return extractData(response);
  },

  // 单篇文章阅读来源
  getArticleReadSources: async (params: ListQueryRequest & { articleId: string }): Promise<PagedData<any>> => {
    const response = await apiClient.post<ResponseData<PagedData<any>>>('/content/articles/read-sources/list', params);
    return extractData(response);
  },

  // 同步单篇文章互动数据
  syncArticleMetrics: async (articleId: string): Promise<string> => {
    const response = await apiClient.post<ResponseData<string>>(`/content/articles/${articleId}/sync-metrics`, {});
    return extractData(response);
  },

  // 同步单篇文章评论
  syncArticleComments: async (articleId: string): Promise<string> => {
    const response = await apiClient.post<ResponseData<string>>(`/content/articles/${articleId}/sync-comments`, {});
    return extractData(response);
  },

  // AI审查文章内容
  reviewArticle: async (articleId: string): Promise<any> => {
    const response = await apiClient.post<ResponseData<any>>('/content/articles/review', { articleId });
    return extractData(response);
  },

  // 提示词模板分类
  getPromptTemplateCategories: async (): Promise<any[]> => {
    const response = await apiClient.get<ResponseData<any[]>>('/prompt-templates/categories');
    return extractData(response);
  },

  getPromptTemplateCategoryById: async (id: string): Promise<any> => {
    const response = await apiClient.get<ResponseData<any>>(`/prompt-templates/categories/${id}`);
    return extractData(response);
  },

  createPromptTemplateCategory: async (data: any): Promise<any> => {
    const response = await apiClient.post<ResponseData<any>>('/prompt-templates/categories', data);
    return extractData(response);
  },

  updatePromptTemplateCategory: async (id: string, data: any): Promise<any> => {
    const response = await apiClient.put<ResponseData<any>>(`/prompt-templates/categories/${id}`, data);
    return extractData(response);
  },

  deletePromptTemplateCategory: async (id: string): Promise<any> => {
    const response = await apiClient.delete<ResponseData<any>>(`/prompt-templates/categories/${id}`);
    return extractData(response);
  },
};

// ============ 粉丝分析 API ============
export const fanApi = {
  // 粉丝增长
  getFanGrowth: async (params: ListQueryRequest & { startDate?: string; endDate?: string }): Promise<PagedData<any>> => {
    const response = await apiClient.get<ResponseData<PagedData<any>>>('/fan/growth', { params });
    return extractData(response);
  },
  
  getFanGrowthStats: async (): Promise<{ totalFans: number; newFans: number; unfollowFans: number; netGrowth: number }> => {
    const response = await apiClient.get<ResponseData<any>>('/fan/growth/stats');
    return extractData(response);
  },
  
  // 用户活跃
  getUserActivity: async (params: ListQueryRequest & { startDate?: string; endDate?: string }): Promise<PagedData<any>> => {
    const response = await apiClient.get<ResponseData<PagedData<any>>>('/fan/activity', { params });
    return extractData(response);
  },
  
  getReadSourceStats: async (): Promise<any[]> => {
    const response = await apiClient.get<ResponseData<any[]>>('/fan/read-source');
    return extractData(response);
  },

  // 粉丝画像
  getFanProfiles: async (params: ListQueryRequest & { year?: number }): Promise<PagedData<any>> => {
    const response = await apiClient.get<ResponseData<PagedData<any>>>('/fan/profile', { params });
    return extractData(response);
  },

  // 粉丝增长事件
  getFanGrowthEvents: async (params: ListQueryRequest & { eventType?: string; startDate?: string; endDate?: string; isKeyEvent?: boolean }): Promise<PagedData<any>> => {
    const response = await apiClient.post<ResponseData<PagedData<any>>>('/fan/growth-events/list', params);
    return extractData(response);
  },
};

// Dashboard 类型定义
export interface HotArticleDto {
  id: string;
  title: string;
  publishDate: string;
  readUsers: number;
  completionRate?: number;
  shareCount?: number;
}

export interface KpiData {
  totalReads: number;
  totalReadsTrend?: string;
  readGrowthRate?: number;
  totalFans: number;
  totalFansTrend?: string;
  fanNetGrowth?: number;
  totalArticles: number;
  totalArticlesTrend?: string;
  newArticlesThisMonth?: number;
  totalShares?: number;
  totalSharesTrend?: string;
}

export interface DashboardOverviewDto {
  kpis: KpiData;
  hotArticles: HotArticleDto[];
}

// ============ 友校对比 API ============
export const peerApi = {
  getPeerUniversityStats: async (params: ListQueryRequest & { universityName?: string; dataYear?: number; dataMonth?: number }): Promise<PagedData<any>> => {
    const response = await apiClient.post<ResponseData<PagedData<any>>>('/peer-universities/list', params);
    return extractData(response);
  },
};

// ============ 平台统计 API ============
export const platformApi = {
  getPlatformStats: async (params: ListQueryRequest & { startDate?: string; endDate?: string; platformName?: string }): Promise<PagedData<any>> => {
    const response = await apiClient.get<ResponseData<PagedData<any>>>('/platform/stats', { params });
    return extractData(response);
  },
};

// ============ Dashboard API ============
export const dashboardApi = {
  // 核心指标
  getKPIs: async (): Promise<{
    totalReads: number;
    totalFans: number;
    totalArticles: number;
    growthRate: number;
  }> => {
    const response = await apiClient.get<ResponseData<any>>('/dashboard/kpis');
    return extractData(response);
  },
  
  // 阅读趋势
  getReadTrend: async (days: number = 30): Promise<any[]> => {
    const response = await apiClient.get<ResponseData<any[]>>('/dashboard/read-trend', { params: { days } });
    return extractData(response);
  },
  
  // 热门文章
  getHotArticles: async (limit: number = 10): Promise<any[]> => {
    const response = await apiClient.get<ResponseData<any[]>>('/dashboard/hot-articles', { params: { limit } });
    return extractData(response);
  },
  
  // 数据概览
  getOverview: async (): Promise<any> => {
    const response = await apiClient.get<ResponseData<any>>('/dashboard/overview');
    return extractData(response);
  },
};

// ============ 用户管理 API ============
export const userApi = {
  getUsers: async (params: ListQueryRequest): Promise<PagedData<UserSummary>> => {
    const response = await apiClient.post<ResponseData<PagedData<UserSummary>>>('/users/list', params);
    return extractData(response);
  },
  
  getUserById: async (id: string): Promise<UserInfo> => {
    const response = await apiClient.get<ResponseData<UserInfo>>(`/users/${id}`);
    return extractData(response);
  },
  
  createUser: async (data: CreateUserRequest): Promise<UserSummary> => {
    const response = await apiClient.post<ResponseData<UserSummary>>('/users', data);
    return extractData(response);
  },
  
  updateUser: async (id: string, data: UpdateUserRequest): Promise<void> => {
    await apiClient.put(`/users/${id}`, data);
  },
  
  updatePassword: async (id: string, data: UpdatePasswordRequest): Promise<void> => {
    await apiClient.put(`/users/${id}/password`, data);
  },
  
  assignRoles: async (id: string, data: AssignRolesRequest): Promise<void> => {
    await apiClient.put(`/users/${id}/roles`, data);
  },
  
  activateUser: async (id: string): Promise<void> => {
    await apiClient.post(`/users/${id}/activate`);
  },
  
  deactivateUser: async (id: string): Promise<void> => {
    await apiClient.post(`/users/${id}/deactivate`);
  },
  
  deleteUser: async (id: string): Promise<void> => {
    await apiClient.delete(`/users/${id}`);
  },
};

// ============ 角色管理 API ============
export const roleApi = {
  getRoles: async (params: ListQueryRequest): Promise<PagedData<RoleSummary>> => {
    const response = await apiClient.post<ResponseData<PagedData<RoleSummary>>>('/roles/list', params);
    return extractData(response);
  },
  
  getRoleById: async (id: string): Promise<RoleSummary> => {
    const response = await apiClient.get<ResponseData<RoleSummary>>(`/roles/${id}`);
    return extractData(response);
  },
  
  getPermissions: async (): Promise<Permission[]> => {
    const response = await apiClient.get<ResponseData<Permission[]>>('/roles/permissions');
    return extractData(response);
  },
  
  createRole: async (data: CreateRoleRequest): Promise<RoleSummary> => {
    const response = await apiClient.post<ResponseData<RoleSummary>>('/roles', data);
    return extractData(response);
  },
  
  updateRole: async (id: string, data: UpdateRoleRequest): Promise<void> => {
    await apiClient.put(`/roles/${id}`, data);
  },
  
  updateRolePermissions: async (id: string, data: UpdateRolePermissionsRequest): Promise<void> => {
    await apiClient.put(`/roles/${id}/permissions`, data);
  },
  
  deleteRole: async (id: string): Promise<void> => {
    await apiClient.delete(`/roles/${id}`);
  },
};

// ============ 维度管理 API ============
export const dimensionApi = {
  getDimensions: async (params: ListQueryRequest): Promise<PagedData<any>> => {
    const response = await apiClient.get<ResponseData<PagedData<any>>>('/dimensions', { params });
    return extractData(response);
  },
  
  getDimensionById: async (id: string): Promise<any> => {
    const response = await apiClient.get<ResponseData<any>>(`/dimensions/${id}`);
    return extractData(response);
  },
  
  createDimension: async (data: any): Promise<void> => {
    await apiClient.post('/dimensions', data);
  },
  
  updateDimension: async (id: string, data: any): Promise<void> => {
    await apiClient.put(`/dimensions/${id}`, data);
  },
  
  deleteDimension: async (id: string): Promise<void> => {
    await apiClient.delete(`/dimensions/${id}`);
  },
  
  addField: async (dimensionId: string, data: any): Promise<void> => {
    await apiClient.post(`/dimensions/${dimensionId}/fields`, data);
  },
  
  updateField: async (dimensionId: string, fieldId: string, data: any): Promise<void> => {
    await apiClient.put(`/dimensions/${dimensionId}/fields/${fieldId}`, data);
  },
  
  deleteField: async (dimensionId: string, fieldId: string): Promise<void> => {
    await apiClient.delete(`/dimensions/${dimensionId}/fields/${fieldId}`);
  },
};

// ============ 数据导入 API ============
export const importApi = {
  uploadFile: async (dimensionCode: string, file: File, onProgress?: (progress: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dimensionCode', dimensionCode);
    
    return apiClient.post('/import/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  },
  
  getImportTasks: async (params: ListQueryRequest): Promise<PagedData<any>> => {
    const response = await apiClient.get<ResponseData<PagedData<any>>>('/import/tasks', { params });
    return extractData(response);
  },
  
  getImportTaskById: async (id: string): Promise<any> => {
    const response = await apiClient.get<ResponseData<any>>(`/import/tasks/${id}`);
    return extractData(response);
  },
  
  downloadTemplate: async (dimensionCode: string): Promise<Blob> => {
    const response = await apiClient.get(`/import/template/${dimensionCode}`, { 
      responseType: 'blob' 
    });
    return response.data;
  },
  
  downloadErrorReport: async (taskId: string): Promise<Blob> => {
    const response = await apiClient.get(`/import/tasks/${taskId}/errors`, { 
      responseType: 'blob' 
    });
    return response.data;
  },
};

// ============ 报告管理 API ============
export const reportApi = {
  getDimensions: async (): Promise<Array<{ code: string; name: string; description: string; icon: string; color: string; isActive: boolean }>> => {
    const response = await apiClient.get<ResponseData<Array<{ code: string; name: string; description: string; icon: string; color: string; isActive: boolean }>>>('/reports/dimensions');
    return extractData(response);
  },

  getReports: async (params: ListQueryRequest): Promise<PagedData<any>> => {
    const response = await apiClient.get<ResponseData<PagedData<any>>>('/reports', { params });
    return extractData(response);
  },
  
  getReportById: async (id: string): Promise<any> => {
    const response = await apiClient.get<ResponseData<any>>(`/reports/${id}`);
    return extractData(response);
  },
  
  createReport: async (data: any): Promise<void> => {
    await apiClient.post('/reports', data);
  },
  
  updateReport: async (id: string, data: any): Promise<void> => {
    await apiClient.put(`/reports/${id}`, data);
  },
  
  submitReport: async (id: string): Promise<void> => {
    await apiClient.post(`/reports/${id}/submit`);
  },
  
  approveReport: async (id: string, data: { comment?: string }): Promise<void> => {
    await apiClient.post(`/reports/${id}/approve`, data);
  },
  
  rejectReport: async (id: string, data: { reason: string }): Promise<void> => {
    await apiClient.post(`/reports/${id}/reject`, data);
  },
  
  deleteReport: async (id: string): Promise<void> => {
    await apiClient.delete(`/reports/${id}`);
  },
  
  // AI生成报表
  generateReport: async (description: string): Promise<{ taskId: string; webSocketUrl: string }> => {
    const response = await apiClient.post<ResponseData<{ taskId: string; webSocketUrl: string }>>('/reports/generate', { description });
    return extractData(response);
  },
  
  // 导出报表
  exportReport: async (id: string, format: 'pdf' | 'excel' | 'word'): Promise<Blob> => {
    const response = await apiClient.get(`/reports/${id}/export`, {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  },
};

// ============ 报告管理 New API ============
export const reportNewApi = {
  // 分析用户需求
  analyzeRequirements: async (requirements: string): Promise<{
    dimensions: Array<{
      key: string;
      name: string;
      description: string;
      tables: string[];
      available: boolean;
    }>;
    missingDimensions: string[];
    canGenerate: boolean;
    summary: string;
  }> => {
    const response = await apiClient.post<ResponseData<any>>('/reports-new/analyze', { requirements });
    return extractData(response);
  },
  
  // 生成报告
  generateReport: async (data: {
    requirements: string;
    dimensions: string[];
  }): Promise<{
    id: string;
    title: string;
    content: string;
    downloadUrl?: string;
  }> => {
    const response = await apiClient.post<ResponseData<any>>('/reports-new/generate', data);
    return extractData(response);
  },
  
  // 下载报告
  downloadReport: async (id: string): Promise<Blob> => {
    const response = await apiClient.get(`/reports-new/${id}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  // 获取报告历史
  getReportHistory: async (params: ListQueryRequest): Promise<PagedData<any>> => {
    const response = await apiClient.get<ResponseData<PagedData<any>>>('/reports-new/history', { params });
    return extractData(response);
  },
};

// ============ 公众号管理 API ============
export const wechatAccountApi = {
  getAccounts: async (): Promise<any[]> => {
    const response = await apiClient.get<ResponseData<any[]>>('/content/wechat-accounts');
    return extractData(response);
  },

  searchMptextAccounts: async (keyword: string): Promise<any> => {
    const response = await apiClient.get<ResponseData<any>>(`/mptext/accounts/search?keyword=${encodeURIComponent(keyword)}`);
    return extractData(response);
  },

  createAccount: async (data: any): Promise<any> => {
    const response = await apiClient.post<ResponseData<any>>('/content/wechat-accounts', data);
    return extractData(response);
  },

  updateAccount: async (id: string, data: any): Promise<any> => {
    const response = await apiClient.put<ResponseData<any>>(`/content/wechat-accounts/${id}`, data);
    return extractData(response);
  },

  deleteAccount: async (id: string): Promise<any> => {
    const response = await apiClient.delete<ResponseData<any>>(`/content/wechat-accounts/${id}`);
    return extractData(response);
  },

  getCategories: async (): Promise<any[]> => {
    const response = await apiClient.get<ResponseData<any[]>>('/content/wechat-account-categories');
    return extractData(response);
  },

  createCategory: async (data: any): Promise<any> => {
    const response = await apiClient.post<ResponseData<any>>('/content/wechat-account-categories', data);
    return extractData(response);
  },

  updateCategory: async (id: string, data: any): Promise<any> => {
    const response = await apiClient.put<ResponseData<any>>(`/content/wechat-account-categories/${id}`, data);
    return extractData(response);
  },

  deleteCategory: async (id: string): Promise<any> => {
    const response = await apiClient.delete<ResponseData<any>>(`/content/wechat-account-categories/${id}`);
    return extractData(response);
  },

  syncArticles: async (accountId: string): Promise<{ jobId: string }> => {
    const response = await apiClient.post<ResponseData<{ jobId: string }>>(`/content/wechat-accounts/${accountId}/sync-articles`, {});
    return extractData(response);
  },

  getSyncProgress: async (jobId: string): Promise<{
    jobId: string;
    totalArticles: number;
    savedArticles: number;
    isRunning: boolean;
    isCompleted: boolean;
    errorMessage?: string | null;
  }> => {
    const response = await apiClient.get<ResponseData<any>>(`/content/sync-articles/${jobId}/progress`);
    return extractData(response);
  },
};

// ============ WebSocket 连接（用于报表生成进度）==========
export const createWebSocket = (url: string): WebSocket => {
  const token = localStorage.getItem('accessToken');
  const wsUrl = `${url}?token=${token}`;
  return new WebSocket(wsUrl);
};

// ============ 导出默认实例 ============
export default apiClient;
